'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The Bamboo Plate Is Melamine in 20 Cases Out of 21"
      author="MicroPlastics Research Desk"
      date="August 24, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/home.webp"
      heroImageAlt="Tableware sold as bamboo is usually melamine-formaldehyde resin with bamboo powder as filler, and a 2025 analysis detected melamine in 20 of 21 articles declared as bamboo"
    >
      <BlogLastUpdated iso="2026-08-24" formatted="August 24, 2026" />

      <BlogTLDR>
        <strong>The standard advice for buying bamboo tableware — look for
        &ldquo;100% bamboo&rdquo; and avoid the composite — is advice the newest
        evidence says you cannot reliably follow.</strong>{' '}
        A team at the University of Chemistry and Technology in Prague analysed{' '}
        <strong>33 bio-based food contact articles</strong> and published the
        results in <em>Food Control</em> in February 2025.{' '}
        <strong>Melamine was detected in 20 of the 21 samples sold as
        bamboo.</strong> Their wording is blunt: melamine is{' '}
        <em>&ldquo;still widely present in food contact articles (FCAs) declared
        as being bamboo&rdquo;</em>. That is a labelling problem more than a
        chemistry problem — the chemistry of melamine-formaldehyde resin has
        been understood for decades. What has changed is that the word
        &ldquo;bamboo&rdquo; is now printed on it.{' '}
        <strong>Be careful with the number, though:</strong> 20 of 21 is
        melamine <em>present in the article</em>. Six of the 33 products
        actually exceeded EU migration limits. Those are different findings and
        most coverage merges them. Separately, and less known:{' '}
        <strong>bamboo flour was never authorised as an additive in food-contact
        plastics in the EU at all</strong>, which means the composite is not a
        legal food-contact plastic there regardless of what it migrates. The
        European Commission&rsquo;s coordinated action notified{' '}
        <strong>748 cases</strong> of it.{' '}
        <strong>Real 100% bamboo — no resin binder — remains a fine
        material.</strong> The finding is about whether the label tells you
        which one you are holding.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Melamine was detected in 20 of 21 articles sold as bamboo</strong> in a 2025 <em>Food Control</em> analysis of 33 bio-based food contact articles, 14 of them intended for children.</>,
          <><strong>Detection is not the same as exceeding a limit.</strong> Six of the 33 products exceeded EU specific migration limits. The 20-of-21 figure is about what the material is, not about how much came out.</>,
          <><strong>&ldquo;Bamboo&rdquo; tableware is normally melamine-formaldehyde resin</strong> with bamboo powder as filler. The resin is the structure; the bamboo is the additive.</>,
          <><strong>The EU never authorised bamboo flour in plastics.</strong> Regulation (EU) 10/2011 works from a Union list of over 900 permitted substances and bamboo is not on it, so the composite is unauthorised on legal grounds rather than on test results.</>,
          <><strong>Bamboo is excluded because it is a grass.</strong> The existing wood-flour authorisation (FCM No 96) does not extend to Poaceae, which is the actual mechanism behind the ban.</>,
          <><strong>748 cases were notified</strong> in the Commission&rsquo;s year-long coordinated action to April 2022 across 21 countries — 644 on the EU market, 104 stopped at borders, and 580 sold primarily online.</>,
          <><strong>Heat and acid are the trigger, not time on the shelf.</strong> The EU test uses 3% acetic acid at 70&nbsp;°C precisely because that is the condition melamine plastics fail under.</>,
          <><strong>Melamine tableware is a thermoset.</strong> It cannot be remelted, and the documented hazard is monomer migration rather than particle shedding — particle release from it has not been measured.</>,
          <><strong>The material is not the villain of this story. The label is.</strong> Genuine binder-free bamboo, stainless steel and glazed ceramic all sidestep the question entirely.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: '20 of 21', label: 'articles sold as “bamboo” with melamine detected', context: 'detection of melamine in the article — not the same as exceeding a migration limit', source: 'Bechynska et al., Food Control, February 2025', sourceUrl: 'https://doi.org/10.1016/j.foodcont.2025.111188' },
          { value: '6 of 33', label: 'products that exceeded EU legal migration limits', context: 'the migration finding, which is the smaller and more often misquoted of the two numbers', source: 'Bechynska et al., 2025', sourceUrl: 'https://doi.org/10.1016/j.foodcont.2025.111188' },
          { value: '748', label: 'cases of unauthorised bamboo powder in plastic food contact materials notified', context: '644 found on the EU market, 104 rejected at borders, 580 sold primarily online', source: 'European Commission, “Bamboo-zling” coordinated action, to April 2022', sourceUrl: 'https://food.ec.europa.eu/food-safety/eu-agri-food-fraud-network/eu-coordinated-actions/bamboo-zling_en' },
          { value: '25×', label: 'the formaldehyde migration limit, in tested coffee cups', context: 'laboratory analysis of Chinese-manufactured coffee cups specifically; melamine in the same cups came out at 3.5× the limit', source: 'European Commission, “Bamboo-zling”', sourceUrl: 'https://food.ec.europa.eu/food-safety/eu-agri-food-fraud-network/eu-coordinated-actions/bamboo-zling_en' },
          { value: '2.5 / 15 mg/kg', label: 'EU specific migration limits for melamine and formaldehyde', context: 'set under Regulation (EU) 10/2011 on plastics intended to contact food', source: 'Commission Regulation (EU) No 10/2011', sourceUrl: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02011R0010-20200923' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        If you bought bamboo plates or a bamboo travel cup to get away from
        plastic, the odds are you bought plastic. Not as a scam in most cases,
        and not always illegally — but as a matter of what the object is made
        of. Tableware marketed as bamboo is, with few exceptions,
        melamine-formaldehyde resin that has had bamboo powder mixed into it.
        The resin holds the shape. The bamboo is filler.
      </p>
      <p>
        We have said the sensible-sounding version of this before, on our{' '}
        <Link href="/blog/microplastics-in-sippy-cups-and-toddler-dishes" className="text-forest underline">toddler dishware page</Link>:
        avoid bamboo-melamine composite, look for &ldquo;100% bamboo&rdquo;. The
        2025 analysis is the reason to revisit that. If melamine turns up in 20
        of 21 articles declared as bamboo, then reading the front of the box is
        not a test, and telling people to read the front of the box is not
        advice. It is the same failure mode as{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">BPA-free</Link>,{' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">Tritan</Link>,{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-forest underline">&ldquo;microwave safe&rdquo;</Link>{' '}
        and{' '}
        <Link href="/blog/is-recycled-plastic-safer" className="text-forest underline">&ldquo;100% recycled&rdquo;</Link>:
        a reassuring word that turns out to describe something other than what
        the reader thought it described.
      </p>

      <h2>What the 2025 analysis actually found</h2>
      <p>
        The study is Bechynska and colleagues at the University of Chemistry and
        Technology in Prague, published in <em>Food Control</em> in February
        2025. It is a laboratory analysis of products bought from the market —
        not a review, not a model — which makes it directly quotable in a way
        that much of this field is not.
      </p>
      <p>
        They took <strong>33 bio-based food contact articles</strong>: 21 made
        of bamboo and 12 from other bio-based feedstocks, sourced across the
        Czech Republic, the United Kingdom and China.{' '}
        <strong>Fourteen of the 33 were intended for children.</strong>
      </p>
      <p>
        <strong>Melamine was detected in 20 of the 21 bamboo samples</strong>,
        and in 2 of the 12 other bio-based products. Separately, on migration
        testing, <strong>six products exceeded EU legal migration limits</strong>.
      </p>
      <p>
        Those two sentences are the whole story and they are constantly welded
        together into a third sentence that is false. Twenty of twenty-one is a
        statement about <em>composition</em>: melamine was in the article. Six of
        thirty-three is a statement about <em>migration</em>: that much came out
        under test conditions into a food simulant. A plate can be melamine and
        still pass. Most of them did.
      </p>
      <p>
        The authors also ran a broader screen and tentatively identified{' '}
        <strong>79 compounds</strong>, of which 55 were plant metabolites — what
        you would expect from ground-up plant matter — and 24 were not plant
        material at all, including phthalates, triethanolamine, pesticide
        residues and mycotoxins. A targeted screen of{' '}
        <strong>442 pesticides</strong> found benzalkonium chlorides most
        frequently, alongside insecticides and fungicides. That is the part of
        the paper that gets no coverage, and it is a reminder that agricultural
        filler is agricultural.
      </p>

      <h2>Why the EU calls it unauthorised rather than unsafe</h2>
      <p>
        This is the half of the story almost nobody covers, and it is the more
        decisive half, because it does not depend on any migration result at
        all.
      </p>
      <p>
        Plastic food contact materials in the EU are governed by{' '}
        <strong>Regulation (EU) 10/2011</strong>, which works on a positive
        list. Article 5 requires that substances used are on a Union list of
        authorised substances — over 900 of them. Anything not on that list is
        not permitted, and the burden is not on regulators to prove harm. The
        Commission&rsquo;s own framing is that{' '}
        <em>&ldquo;most plant-based additives, including bamboo, have not been
        safety assessed for their use in plastics&rdquo;</em>.
      </p>
      <p>
        There is an authorisation for wood flour — FCM No 96 — and the obvious
        question is why it does not cover bamboo. The answer is botanical:{' '}
        <strong>bamboo is a grass</strong>, a member of the Poaceae family,
        rather than a tree or shrub. EFSA concluded the wood-fibre authorisation
        could not be stretched across that gap and would need case-by-case
        evaluation. The expert working group&rsquo;s conclusion was that{' '}
        <em>&ldquo;no legal basis exists for the use of bamboo flour as an
        additive in plastics&rdquo;</em>.
      </p>
      <p>
        So a bamboo-melamine cup is not banned in Europe because it was tested
        and failed. It is unauthorised because nobody ever established that the
        filler was safe to put in a food-contact plastic, and the law does not
        allow that gap to be filled by assumption.
      </p>

      <h2>How big the problem turned out to be</h2>
      <p>
        The Commission ran a coordinated enforcement action with the memorable
        name <strong>Bamboo-zling</strong>. It lasted one year, ending in April
        2022, and involved 21 countries across the EU plus Norway.
      </p>
      <p>
        The result: <strong>748 cases</strong> of plastic food contact materials
        containing unauthorised bamboo powder were notified.{' '}
        <strong>644 were found on the EU market</strong>, 104 were rejected at
        borders — mostly arriving from China — and{' '}
        <strong>580 were sold primarily online</strong>, which is the detail
        worth holding onto, because online marketplaces are exactly where this
        category still lives. Products were subject to destruction, recall or
        removal, and businesses were required to withdraw them immediately.
      </p>
      <p>
        On the migration side, laboratory analysis of Chinese-manufactured
        coffee cups found <strong>melamine at 3.5 times</strong> and{' '}
        <strong>formaldehyde at 25 times</strong> the maximum permitted levels.
        That figure deserves a fence around it: it describes those coffee cups,
        not bamboo tableware as a class. Separately, more than 50 RASFF
        notifications have concerned high formaldehyde migration from these
        products, and over 10% of those exceeded the specific migration limit by
        more than a factor of ten.
      </p>

      <h2>What melamine actually does, and what it does not</h2>
      <p>
        Melamine tableware is a <strong>thermoset</strong>. Melamine and
        formaldehyde are cured into a rigid network that cannot be melted and
        re-formed, which is why a melamine plate feels like ceramic, weighs
        almost nothing and survives being dropped. It is also why melamine does
        not behave like the polypropylene in a{' '}
        <Link href="/blog/is-rubbermaid-safe-microplastics" className="text-forest underline">food storage container</Link>.
      </p>
      <p>
        The established concern in the regulatory literature is{' '}
        <strong>monomer migration</strong> — unreacted melamine and formaldehyde
        leaving the surface into food — and not particle shedding. We are
        deliberate about this on our{' '}
        <Link href="/materials/melamine" className="text-forest underline">melamine material page</Link>{' '}
        too: particle release from melamine specifically has not been measured.
        Anyone quoting you a microplastics-per-meal figure for a melamine plate
        has made it up.
      </p>
      <p>
        The conditions that drive migration are well characterised, and they are
        the reason the EU compliance test looks the way it does:{' '}
        <strong>3% acetic acid at 70&nbsp;°C for two hours, repeated</strong>.
        Acetic acid is used because it is the most aggressive simulant against
        melamine plastics, and 70&nbsp;°C is used because these articles are
        sold for hot food and drink. Heat plus acid is the failure mode. Hot
        tomato sauce, coffee, curry, vinegary salad — and the dishwasher, which
        applies heat and detergent over and over across a plate&rsquo;s life.
      </p>
      <p>
        Both substances have regulatory weight behind them: formaldehyde is
        classified as carcinogenic, and melamine as possibly carcinogenic to
        humans. That is a hazard classification, not a statement about the dose
        from one plate — but it is why the limits are set at{' '}
        <strong>2.5 mg/kg for melamine and 15 mg/kg for formaldehyde</strong>{' '}
        rather than left to manufacturers.
      </p>

      <h2>Tableware materials, ranked for what actually contacts food</h2>

      <BlogComparisonTable
        caption="Tableware materials, ranked by what reaches hot or acidic food"
        columns={[
          { key: 'material', label: 'Material' },
          { key: 'verdict', label: 'Verdict' },
          { key: 'notes', label: 'What to know' },
        ]}
        rows={[
          { material: 'Glazed ceramic / stoneware', verdict: 'No migration concern of this kind', notes: 'Heavy and breakable, which is the entire trade-off. Check glaze provenance on very cheap imported ware.' },
          { material: 'Stainless steel', verdict: 'No migration concern of this kind', notes: 'The practical answer for children, picnics and camping — the three uses melamine owns.' },
          { material: 'Borosilicate or tempered glass', verdict: 'No migration concern of this kind', notes: 'Handles heat and acid without either. See our glass comparison for the difference between the two.' },
          { material: 'Genuine 100% bamboo, no binder', verdict: 'Fine as a material', notes: 'Real, and genuinely plastic-free. The problem is verification, not the plant. Usually a solid piece, not moulded.' },
          { material: 'Food-grade silicone', verdict: 'Low', notes: 'Different chemistry again; useful for children’s plates with suction bases.' },
          { material: 'Melamine, plainly labelled', verdict: 'Conditional — never hot, acidic or microwaved', notes: 'Cold and dry service only. Every manufacturer prints “not microwave safe” for this reason.' },
          { material: '“Bamboo”, “wheat straw” or “plant fibre” composite', verdict: 'Treat as melamine', notes: 'Melamine-formaldehyde with plant filler. Unauthorised in the EU. Melamine was detected in 20 of 21 such articles in 2025.' },
          { material: 'Scratched or chipped melamine of any kind', verdict: 'Replace', notes: 'A thermoset cannot reflow, so surface damage is permanent and it is a food-contact surface.' },
        ]}
      />

      <h2>What to do with the ones you already own</h2>
      <p>
        Nothing about this justifies throwing out a cupboard tonight. Migration
        from melamine is a heat-and-acid phenomenon, which means it is a{' '}
        <em>usage</em> problem far more than an ownership problem, and usage is
        the part you control.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Never microwave it.</strong> Not for thirty seconds, not to
          take the chill off. This is the single highest-yield rule and it is
          already moulded into the base of most melamine ware.
        </li>
        <li>
          <strong>Keep it away from hot and acidic food.</strong> Melamine is
          genuinely good at cold, dry and unbreakable — picnics, salads,
          crackers, a toddler&rsquo;s sandwich. It is poorly suited to soup,
          tomato sauce, coffee and curry, which is exactly what bamboo travel
          cups are sold for.
        </li>
        <li>
          <strong>Retire anything scratched or chipped.</strong> A thermoset
          cannot heal or be reflowed, so damage to the surface is permanent, and
          that surface is what your food sits on.
        </li>
        <li>
          <strong>Hand-wash rather than dishwash</strong> where you can.
          Repeated heat and detergent cycles are cumulative exposure for the
          plate as much as for you.
        </li>
        <li>
          <strong>Replace the children&rsquo;s set first.</strong> Fourteen of
          the 33 articles in the 2025 analysis were intended for children, and
          intake per kilogram of body weight is highest in exactly that group —
          which is the argument our{' '}
          <Link href="/blog/microplastics-and-children" className="text-forest underline">children and microplastics</Link>{' '}
          page makes at length.
        </li>
        <li>
          <strong>Buy the boring materials.</strong> Stainless steel, glazed
          ceramic and glass end the question rather than answering it. For
          lunches specifically, our{' '}
          <Link href="/blog/back-to-school-microplastics-guide" className="text-forest underline">back-to-school lunchbox guide</Link>{' '}
          and{' '}
          <Link href="/blog/best-plastic-free-food-storage" className="text-forest underline">plastic-free food storage</Link>{' '}
          cover the swaps.
        </li>
      </ul>

      <h2>How to tell what you are holding</h2>
      <p>
        Since the label is unreliable, the physical tells are worth knowing.
        Melamine ware is <strong>far too light for how ceramic it feels</strong>,
        and it does not chime when tapped — it thuds. It is almost always
        moulded, with a smooth uniform curve and a moulding seam, rather than
        turned or carved from a solid piece. Look at the base: the pairing of
        the word &ldquo;melamine&rdquo; with &ldquo;not for microwave use&rdquo;
        is close to universal, and the microwave warning on its own is a strong
        signal even when the word melamine is absent.
      </p>
      <p>
        Genuine binder-free bamboo tends to show grain that runs continuously
        through the piece, feels warmer and more variable, and is usually sold
        as a board, bowl or utensil rather than as a moulded plate with a
        printed pattern. If a &ldquo;bamboo&rdquo; item is glossy, perfectly
        coloured, printed with a cartoon and moulded to a shape bamboo does not
        naturally take, it is a composite.
      </p>

      <p>
        Related reading:{' '}
        <Link href="/blog/microplastics-in-sippy-cups-and-toddler-dishes" className="text-forest underline">toddler dishware ranked</Link>,{' '}
        <Link href="/blog/is-it-plastic" className="text-forest underline">is it plastic?</Link>,{' '}
        <Link href="/blog/microplastics-in-cooking-utensils" className="text-forest underline">cooking utensils</Link>,{' '}
        <Link href="/blog/best-non-toxic-cookware" className="text-forest underline">non-toxic cookware</Link>,{' '}
        <Link href="/products/melamine-tableware" className="text-forest underline">our melamine tableware entry</Link>, and{' '}
        <Link href="/materials/melamine" className="text-forest underline">melamine as a material</Link>.
      </p>

      <BlogAppCTA
        headline="Check a plate before you serve on it"
        sub="Scan tableware, storage and utensils with MicroPlastics for a 0&ndash;100 score based on material, heat exposure and condition &mdash; the three things a &ldquo;bamboo&rdquo; label will not tell you."
        buttonLabel="Scan my tableware"
      />

      <BlogFAQ
        items={[
          {
            question: 'Is bamboo tableware actually made of plastic?',
            answer: 'Usually yes. Tableware sold as bamboo is normally melamine-formaldehyde resin with bamboo powder mixed in as a filler — the resin provides the structure and the bamboo is an additive. A 2025 analysis published in Food Control detected melamine in 20 of the 21 food contact articles it tested that were declared as bamboo. Genuine binder-free bamboo products exist, but they are typically carved or laminated solid pieces rather than moulded plates.',
          },
          {
            question: 'Did 20 of 21 bamboo products fail safety limits?',
            answer: 'No, and this is the most common misreading. Melamine was detected in 20 of 21 bamboo samples, which is a statement about what the material contains. Separately, six of the 33 products tested exceeded EU legal migration limits, which is a statement about how much came out under test conditions. Most of the melamine-containing products did not exceed the limits.',
          },
          {
            question: 'Why did the EU ban bamboo tableware?',
            answer: 'It was not banned for failing tests. Plastic food contact materials in the EU run on a positive list under Regulation (EU) 10/2011, and bamboo flour is not on that list of over 900 authorised substances. The existing wood-flour authorisation does not extend to it because bamboo is a grass rather than a tree or shrub. The EU expert working group concluded that no legal basis exists for using bamboo flour as an additive in plastics, so the composite is unauthorised on legal grounds.',
          },
          {
            question: 'Do melamine plates release microplastics?',
            answer: 'Particle release from melamine tableware has not been measured, and anyone quoting a particles-per-meal figure for it is inventing one. Melamine is a thermoset — cured into a rigid network that cannot be remelted — so it does not behave like the polypropylene in a food storage container. The documented and regulated concern is migration of unreacted melamine and formaldehyde monomers into hot or acidic food.',
          },
          {
            question: 'Is it safe to drink coffee from a bamboo travel cup?',
            answer: 'That is close to the worst use for one. Hot liquid is exactly the condition that drives melamine and formaldehyde migration, which is why the EU compliance test uses 3% acetic acid at 70 °C. Laboratory analysis of Chinese-manufactured bamboo-melamine coffee cups in the European Commission’s coordinated action found melamine at 3.5 times and formaldehyde at 25 times the permitted limits. Stainless steel or glass is the straightforward swap.',
          },
          {
            question: 'Are melamine dishes safe for children?',
            answer: 'They are acceptable for cold and dry food and poorly suited to hot or acidic meals, which unfortunately describes a lot of what children eat — pasta with sauce, hot oatmeal, fruit purée. Fourteen of the 33 articles in the 2025 analysis were intended for children, and exposure per kilogram of body weight is highest in that age group. Stainless steel or food-grade silicone removes the question entirely.',
          },
          {
            question: 'How can I tell real bamboo from bamboo-melamine composite?',
            answer: 'Physical tells beat labels here. Melamine composite is unnaturally light for how ceramic it feels, thuds rather than chimes when tapped, and is moulded with a visible seam and often a printed pattern. Genuine bamboo shows continuous grain through the piece and is usually a board, bowl or utensil rather than a moulded plate. Check the base for “melamine” or a “not for microwave use” warning — the latter is a strong signal even when the word melamine is absent.',
          },
          {
            question: 'Should I throw out my bamboo plates?',
            answer: 'Not urgently. Migration from melamine is driven by heat and acid, so it is more a usage question than an ownership one. Stop microwaving them, keep them away from hot and acidic food, hand-wash rather than dishwash, and replace any piece that is scratched or chipped, because a thermoset cannot reflow and that damage is permanent. If you replace anything first, make it the children’s set.',
          },
          {
            question: 'Is 100% bamboo without a binder still fine?',
            answer: 'Yes. Bamboo the plant is not the problem in any of this — the finding is about melamine resin being sold under a bamboo name. Genuine binder-free bamboo boards, bowls and utensils remain a reasonable plastic-free choice. The practical difficulty is verifying which one you have, which is why the physical tells matter more than the packaging claim.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Bechynska K, et al.', year: 2025, title: 'Analysis of bio-based food contact articles — 33 articles analysed (21 bamboo, 12 other bio-based, 14 intended for children) sourced from Czechia, the UK and China; melamine detected in 20 of 21 bamboo samples and 2 of 12 other bio-based products; 6 products exceeded EU migration limits; 79 compounds tentatively identified including phthalates, triethanolamine, pesticide residues and mycotoxins; 442 pesticides screened. Verbatim: "melamine is still widely present in food contact articles (FCAs) declared as being bamboo"', publisher: 'Food Control (University of Chemistry and Technology, Prague), DOI 10.1016/j.foodcont.2025.111188', url: 'https://doi.org/10.1016/j.foodcont.2025.111188' },
          { authors: 'European Commission, DG SANTE', year: 2022, title: '“Bamboo-zling” EU coordinated action on plastic food contact materials containing unauthorised bamboo powder — one year to April 2022 across 21 countries; 748 cases notified, 644 found on the EU market, 104 rejected at borders, 580 sold primarily online; laboratory analysis of Chinese-manufactured coffee cups found melamine at 3.5× and formaldehyde at 25× maximum permitted levels; products subject to destruction, recall or removal from market', publisher: 'European Commission — EU Agri-Food Fraud Network', url: 'https://food.ec.europa.eu/food-safety/eu-agri-food-fraud-network/eu-coordinated-actions/bamboo-zling_en' },
          { authors: 'European Commission', year: 2011, title: 'Commission Regulation (EU) No 10/2011 on plastic materials and articles intended to come into contact with food — Union list of authorised substances (Article 5); specific migration limits of 2.5 mg/kg for melamine and 15 mg/kg for formaldehyde; compliance testing for melamine plastics uses 3% acetic acid at 70 °C', publisher: 'EUR-Lex', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02011R0010-20200923' },
          { authors: 'Food Packaging Forum', year: 2021, title: 'Bamboo in plastic FCMs not authorized in the EU — EFSA determined the wood-fibre authorisation (FCM No 96) does not extend to bamboo because bamboo is a grass (Poaceae) rather than tree or shrub wood; expert working group concluded "no legal basis exists for the use of bamboo flour as an additive in plastics"; over 50 RASFF notifications concerning high formaldehyde migration, with over 10% exceeding the specific migration limit by more than a factor of 10', publisher: 'Food Packaging Forum', url: 'https://foodpackagingforum.org/news/bamboo-in-plastic-fcms-not-authorized-in-the-eu' },
          { authors: 'German Federal Institute for Risk Assessment (BfR)', year: 2024, title: 'Questions and answers on tableware and kitchen utensils made of melamine-formaldehyde resin — heating the article, using it for hot foods or washing it in the dishwasher may increase the amounts of melamine and formaldehyde which migrate into foods', publisher: 'BfR', url: 'https://www.bfr.bund.de/en/service/frequently-asked-questions/topic/questions-and-answers-on-tableware-and-kitchen-utensils-made-of-melamine-formaldehyde-resin/' },
        ]}
      />
    </BlogPostLayout>
  )
}
